<template>
  <div class="mobile-draws-section">
    <div class="mobile-draws-header">
      <h3 class="mobile-section-title">
        {{ $t('detail.selectDraws') }}
      </h3>
    </div>

    <div class="mobile-draws-content">
      <div class="mobile-draws-grid">
        <div
          v-for="(draw, index) in allDraws"
          :key="draw.event_id"
          class="mobile-draw-item"
          :class="{ 'selected': isDrawSelected(index) }"
          @click="toggleDraw(index)"
        >
          <div class="mobile-draw-card-content">
            <div class="mobile-draw-info">
              <span class="mobile-draw-date">{{ formatDate(draw.draw_date) }}</span>
              <span class="mobile-draw-time">{{ formatTime(draw.draw_date) }}</span>
            </div>
            <div class="mobile-checkbox-container">
              <div class="mobile-checkbox-custom">
                <svg
                  v-if="isDrawSelected(index)"
                  class="check-mark"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="mobile-separator" />

      <div class="mobile-quick-actions">
        <button
          type="button"
          class="mobile-action-btn primary"
          @click="selectAll"
        >
          {{ $t('draws.selectAll') }}
        </button>
        <button
          type="button"
          class="mobile-action-btn secondary"
          @click="clearSelectedDraws"
        >
          {{ $t('draws.clearAll') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-undef */
import { computed } from 'vue';

const MOBILE_VISIBLE_DRAWS_MAX = 8;

interface Draw {
  event_id: number | string;
  draw_date: string;
  cutoff: string;
}

const props = withDefaults(defineProps<{
  upcomingDraws?: Draw[];
  draws?: Draw[];
  selectedDraws: boolean[];
}>(), {
  upcomingDraws: () => [],
  draws: () => [],
  selectedDraws: () => [],
});

const allDraws = computed(() => {
  const source = props.upcomingDraws?.length ? props.upcomingDraws : (props.draws || []);
  return source.slice(0, MOBILE_VISIBLE_DRAWS_MAX);
});

const emit = defineEmits(['update-selected-draws', 'toggle-draw-selection']);

const isDrawSelected = (index: number) => {
  return !!props.selectedDraws[index];
};

const toggleDraw = (index: number) => {
  emit('toggle-draw-selection', index);
};

const selectAll = () => {
  allDraws.value.forEach((_, index) => {
    if (!props.selectedDraws[index]) {
      emit('update-selected-draws', index, true);
    }
  });
  // Deselect any draws beyond the mobile visible cap
  props.selectedDraws.forEach((selected, index) => {
    if (selected && index >= MOBILE_VISIBLE_DRAWS_MAX) {
      emit('update-selected-draws', index, false);
    }
  });
};

const clearSelectedDraws = () => {
  // Clear all except the first one to ensure minimum selection
  props.selectedDraws.forEach((selected, index) => {
    if (selected && index !== 0) {
      emit('update-selected-draws', index, false);
    }
  });
  // Ensure first is selected if it wasn't
  if (!props.selectedDraws[0]) {
    emit('update-selected-draws', 0, true);
  }
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const formatTime = (dateStr: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const seconds = date.getSeconds().toString().padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
};
</script>

<style scoped>
.mobile-draws-section {
  background: var(--colour-surface-cont-lowest);
  border-radius: var(--detail-panel-radius, 4px);
  overflow: hidden;
  border: var(--detail-panel-border-width, 1px) solid var(--colour-surface-cont);
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;
}

.mobile-draws-header {
  padding: 10px var(--detail-mobile-gutter, 16px);
  background: var(--surface-detail-header);
  border-bottom: 1px solid var(--border-detail-panel);
}

.mobile-section-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text-detail-header);
  margin: 0;
  text-transform: none;
}

.mobile-draws-content {
  padding: var(--detail-mobile-gutter, 12px);
  background: var(--surface-detail-panel);
}

.mobile-draws-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
}

.mobile-draw-item {
  background: var(--surface-detail-card);
  border: 1.5px solid var(--border-detail-card);
  border-radius: 0;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-draw-item.selected {
  border-color: var(--colour-orange-button);
  background: var(--surface-detail-card);
}

.mobile-draw-card-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
}

.mobile-draw-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.mobile-draw-date {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-detail-card);
}

.mobile-draw-time {
  font-size: 13px;
  font-weight: 700;
  color: var(--colour-orange-text-colour);
}

.mobile-checkbox-container {
  display: flex;
  align-items: center;
}

.mobile-checkbox-custom {
  width: 22px;
  height: 22px;
  border: 2px solid var(--colour-surface-cont-high);
  border-radius: 5px;
  background: var(--colour-surface-cont-lowest);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.mobile-draw-item.selected .mobile-checkbox-custom {
  background: var(--colour-orange-button);
  border-color: var(--colour-orange-button);
}

.check-mark {
  width: 14px;
  height: 14px;
  color: var(--text-button);
}

.mobile-separator {
  height: 1px;
  background: var(--colour-surface-cont);
  margin: 12px 0;
}

.mobile-quick-actions {
  display: flex;
  gap: 10px;
}

.mobile-action-btn {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid var(--border-detail-quick-action);
  border-radius: 8px;
  background: var(--surface-detail-quick-action);
  color: var(--text-detail-quick-action);
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-action-btn.primary {
  background: var(--surface-detail-quick-action-primary);
  color: var(--text-detail-quick-action-primary);
  border-color: var(--border-detail-quick-action-primary);
}

.mobile-action-btn.primary:hover {
  background: var(--surface-detail-quick-action-primary-hover);
}

.mobile-action-btn.secondary:hover {
  background: var(--surface-detail-quick-action-hover);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-draws-content {
    padding: 10px;
  }

  .mobile-draws-grid {
    gap: 6px;
  }
  
  .mobile-draw-item {
    padding: 7px 8px;
  }
  
  .mobile-draw-date, .mobile-draw-time {
    font-size: 12px;
  }
  
  .mobile-checkbox-custom {
    width: 20px;
    height: 20px;
  }
  
  .check-mark {
    width: 12px;
    height: 12px;
  }

  .mobile-section-title {
    font-size: 14px;
  }

  .mobile-action-btn {
    font-size: 13px;
    padding: 9px 10px;
  }
}
</style>
